import React from "react";
import classNames from "classnames";
import {PropsRender} from "../Types/PropsRender";
import {FieldDataProps} from "../Types/FieldDataProps";
import Formatter from "./Formatter";
import Editor from "./Editor";
import DefaultEditable from "./DefaultEditable";
import debounce from "../../../../../utils/debounce";
import Tooltip, {TooltipProps} from "../../../../../utils/Tooltip";
import {Items} from "../Types/Items";
import Field from "../Field";

type FieldState = {}

export default class FieldData<P extends FieldDataProps, S extends FieldState> extends Field<P, S> {

    documentEditListener;
    cellClick = false;
    protected element: HTMLElement;
    public onCloseEdit: any;

    constructor(props) {
        super(props);
        this.bindDocumentEditListener = this.bindDocumentEditListener.bind(this);
        this.unbindDocumentEditListener = this.unbindDocumentEditListener.bind(this);
        this.onEdit = this.onEdit.bind(this);
        this.onCloseEdit = debounce(function () {
            this.unbindDocumentEditListener();
            if (this.props.isEditing) {
                this.props.cancelEditor();
            }
        }.bind(this));
        this.onKeyDown = this.onKeyDown.bind(this);
        this.switchCellToViewMode = this.switchCellToViewMode.bind(this);
        this.getFormatter = this.getFormatter.bind(this);
        this.getEditor = this.getEditor.bind(this);
    }


    componentDidUpdate(prevProps: Readonly<FieldDataProps>, prevState: Readonly<FieldState>, snapshot?: any) {
        if (this.props.isEditing) {
            if (!this.getEditor().length) {
                this.props.moveEditorNext();
            } else {
                this.onEdit();
            }
        } else {
            this.onCloseEdit();
        }
    }

    bindDocumentEditListener() {
        this.cellClick = true;
        if (!this.documentEditListener) {
            this.documentEditListener = () => {
                if (!this.cellClick) {
                    this.switchCellToViewMode();
                }

                this.cellClick = false;
            };

            document.addEventListener('click', this.documentEditListener);
        }
    }

    unbindDocumentEditListener() {
        if (this.documentEditListener) {
            document.removeEventListener('click', this.documentEditListener);
            this.documentEditListener = null;
        }
    }

    onEdit(event?) {
        if (!this.props.isEditing && this.getEditor().length) {
            this.props.setEditor();
            event && event.preventDefault();
            event && event.stopPropagation();
            this.bindDocumentEditListener();
            return false;
        } else {
            this.cellClick = true;
        }
    }

    onKeyDown(event) {
        if (this.getEditor().length) {
            // UP
            if (event.which === 38) {
                if (this.props.moveEditorUp) {
                    this.props.moveEditorUp(event);
                }
            }
            // DOWN
            if (event.which === 40) {
                if (this.props.moveEditorDown) {
                    this.props.moveEditorDown(event);
                }
            }
            // ENTER TAB
            if (event.which === 13 || event.which === 9) {
                if (this.props.moveEditorRight && event.which === 9) {
                    event && event.preventDefault();
                    event && event.stopPropagation();
                    this.props.moveEditorRight(event);
                }
                if (this.props.moveEditorDown && event.which === 13) {
                    this.props.moveEditorDown(event);
                }
            }
            // ESC
            if (event.which === 27) {
                if (this.props.cancelEditor) {
                    this.props.cancelEditor();
                }
            }
        }
        if (this.props.onKeyDown) {
            this.props.onKeyDown(event);
        }
    }

    switchCellToViewMode() {
        if (this.props.editorValidator) {
            let valid = this.props.editorValidator(this.props);
            if (valid) {
                this.onCloseEdit();
            }
        }
        else {
            this.onCloseEdit();
        }
    }

    addItems = (items: Items) => {
        return this.props.addItems(items);
    }

    editItems = (items: Items) => {
        return this.props.editItems(items);
    }

    removeItems = (items: Items) => {
        return this.props.removeItems(items);
    }

    getFormatter() {
        let formatter: PropsRender<FieldDataProps>[] = React.Children.map(this.props.children || [], (child: React.ReactElement, idx) => {
            if (!child) return;
            if (child?.type?.prototype instanceof Formatter || child.type === Formatter) {
                return <child.type
                    key={idx}
                    {...child.props}
                    {...this.props}
                    value={this.props.value}
                    editItems={this.editItems}
                    addItems={this.addItems}
                    removeItems={this.removeItems}
                />;
            }
        }).filter(Boolean);
        if (!formatter.length && this.props.formatter) {
            let FormatterComp = this.props.formatter;
            if (FormatterComp instanceof Function) {
                FormatterComp = <FormatterComp key={0} {...this.props} />;
            } else {
                FormatterComp = <FormatterComp.type key={0} ref={FormatterComp.ref} {...FormatterComp.props} {...this.props} />;
            }
            formatter = [FormatterComp];
        }
        return formatter;
    }

    getEditor() {
        if (this.props.disabled) return [];
        let editor: PropsRender<FieldDataProps>[] = React.Children.map(this.props.children || [], (child: React.ReactElement, idx) => {
            if (!child) return;
            if (child?.type?.prototype instanceof Editor || child.type === Editor) {
                return <child.type
                    key={idx}
                    {...child.props}
                    value={this.props.value}
                    editItems={this.editItems}
                    addItems={this.addItems}
                    removeItems={this.removeItems}
                />;
            }
        }).filter(Boolean);
        if (!editor.length) {
            if (this.props.editable === true) {
                editor = [<DefaultEditable
                    key={0}
                    {...this.props}
                />];
            } else if (this.props.editable) {
                let EditorComp = this.props.editable;
                if (EditorComp instanceof Function) {
                    EditorComp = <EditorComp key={0} {...this.props} />;
                } else {
                    EditorComp = <EditorComp.type key={0} ref={EditorComp.ref} {...EditorComp.props} {...this.props} />;
                }
                editor = [EditorComp];
            }
        }
        return editor;
    }

    disableSelection = (event) => {
        if (this.props.disableSelectionChangeEvent) {
            event && event.preventDefault();
            event && event.stopPropagation();
            return false;
        }
    }

    onMouseOver = (event) => {
        if (this.props.onMouseOver) {
            this.props.onMouseOver({...this.props, event});
        }
    }

    onMouseLeave = (event) => {
        if (this.props.onMouseLeave) {
            this.props.onMouseLeave({...this.props, event});
        }
    }

    layout(props, state) {
        let content = null;
        let tooltip: TooltipProps = {};
        if (!props.isAggregation && props.isEditing) {
            content = this.getEditor();
        } else {
            content = this.getFormatter();
            if (!content.length) {
                content = props.value || null;
            }

            let tooltipObject = props.tooltip as TooltipProps;
            if (props.tooltip instanceof React.Component) {
                tooltip.title = <props.tooltip.type {...props.tooltip.props} {...props} />;
            } else if (props.tooltip instanceof Function) {
                tooltip.title = props.tooltip(props);
            } else if (tooltipObject) {
                tooltip = {...tooltipObject, position: tooltipObject.position || "top"};
            }
        }
        let className = props.className;
        if (className instanceof Function) {
            className = className(props);
        }
        let style = props.style;
        if (style instanceof Function) {
            style = style(props);
        }
        if (props.isAggregation && !props.aggregation) {
            return <td
                onClick={this.disableSelection}
                tabIndex={props.tabIndex}
                onMouseOver={this.onMouseOver}
                onMouseLeave={this.onMouseLeave}
                className={classNames(className, "p-editable-column p-cell-editing")}
                style={style}
            >
                <div className="editor-field">
                    <div />
                </div>
            </td>
        }
        return (
            <td
                tabIndex={props.tabIndex}
                style={style}
                onMouseOver={this.onMouseOver}
                onMouseLeave={this.onMouseLeave}
                className={classNames(className, "p-editable-column p-cell-editing")}
                onFocus={this.onEdit}
                onClick={(event) => {
                    this.onEdit(event);
                    this.disableSelection(event);
                }}
                onBlur={this.onCloseEdit}
                onKeyDown={this.onKeyDown}
            >
                <div className={classNames("editor-field", {"editing": props.isEditing})}>
                    <Tooltip {...tooltip}>
                        {content}
                    </Tooltip>
                </div>
            </td>
        );
    }

    render() {
        return this.layout(this.props, this.state);
    }
}
